import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SVGArrowRight, SVGClock, SVGHeart, SVGPlane, SVGSun, SVGCloud, SVGCloudSnow, SVGCloudRain, SVGCalendar, SVGDollarSign } from '../assets/PackageSVG';
import { Modal } from './Modal';
import { useOrigin } from '../context/OriginContext';
import { useProfile } from '../context/ProfileContext';
import DESTINATIONS from '../data/destinations';
import FEATURED_TRIPS from '../data/featuredTrips';
import DESTINATION_GALLERIES from '../data/destinationGalleries';
import type { Destination } from '../types/destination';
import styles from './DestinationExpanded.module.scss';

type WeatherIcon = 'sun' | 'cloud' | 'rain' | 'snow';

type Origin = {
    city?: string;
    airport?: string;
    code?: string;
};

type OriginContextValue = {
    origin?: Origin | null;
};

type MonthlyWeather = {
    name: string;
    temp: number;
    icon: WeatherIcon;
    best: boolean;
};

type FeaturedTrip = {
    slug: string;
    destination?: string;
    tagline?: string;
    image?: string;
    startingPrice?: number;
    match?: number;
    categories?: string[];
    gallery?: string[];
    quickInfo?: {
        season?: string;
    };
};

type DestinationView = {
    slug: string;
    name: string;
    country: string;
    region: string;
    tagline: string;
    image: string;
    price: number;
    match: number;
    bestMonths: string;
    flightTime: string;
    knownFor: string[];
    categories: string[];
    description: string;
    temp: string;
    condition: 'Sunny' | 'Partly Cloudy';
    photos: string[];
    monthlyWeather: MonthlyWeather[];
};

type SimilarDestination = {
    slug: string;
    name: string;
    country: string;
    price: number;
    image: string;
    tagline: string;
};

const destinations = DESTINATIONS as Destination[];
const featuredTrips = FEATURED_TRIPS as FeaturedTrip[];
const destinationGalleries = DESTINATION_GALLERIES as Record<string, string[]>;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

const MONTH_ABBR_MAP: Record<string, number> = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
};

const REGION_TEMPS: Record<string, number[]> = {
    Asia: [25, 26, 28, 30, 31, 31, 30, 30, 29, 28, 27, 25],
    Europe: [4, 5, 9, 13, 18, 22, 25, 24, 20, 15, 9, 5],
    'Latin America': [24, 25, 26, 27, 28, 28, 29, 29, 28, 27, 26, 24],
    Africa: [22, 23, 25, 27, 28, 27, 26, 26, 27, 26, 24, 22],
    USA: [2, 3, 8, 14, 20, 25, 28, 27, 23, 16, 10, 4],
    Oceania: [24, 24, 23, 21, 19, 17, 16, 17, 18, 20, 22, 23],
};

const DEST_COORDS: Record<string, { lat: number; lon: number }> = {
    maldives: { lat: 4.1755, lon: 73.5093 },
    bali: { lat: -8.3405, lon: 115.092 },
    santorini: { lat: 36.3932, lon: 25.4615 },
    tulum: { lat: 20.2115, lon: -87.4654 },
    amalfi: { lat: 40.634, lon: 14.6027 },
    phuket: { lat: 7.8804, lon: 98.3923 },
    cancun: { lat: 21.1619, lon: -86.8515 },
    zanzibar: { lat: -6.1659, lon: 39.2026 },
    hawaii: { lat: 19.8968, lon: -155.5828 },
    mykonos: { lat: 37.4467, lon: 25.3289 },
    paris: { lat: 48.8566, lon: 2.3522 },
    tokyo: { lat: 35.6762, lon: 139.6503 },
    kyoto: { lat: 35.0116, lon: 135.7681 },
    'new york': { lat: 40.7128, lon: -74.006 },
    rome: { lat: 41.9028, lon: 12.4964 },
    barcelona: { lat: 41.3874, lon: 2.1686 },
    london: { lat: 51.5074, lon: -0.1278 },
    dubai: { lat: 25.2048, lon: 55.2708 },
    iceland: { lat: 64.9631, lon: -19.0208 },
    patagonia: { lat: -41.8101, lon: -68.9063 },
    'swiss alps': { lat: 46.8182, lon: 8.2275 },
    'cape town': { lat: -33.9249, lon: 18.4241 },
    marrakech: { lat: 31.6295, lon: -7.9811 },
    istanbul: { lat: 41.0082, lon: 28.9784 },
    singapore: { lat: 1.3521, lon: 103.8198 },
    bangkok: { lat: 13.7563, lon: 100.5018 },
    lisbon: { lat: 38.7223, lon: -9.1393 },
    amsterdam: { lat: 52.3676, lon: 4.9041 },
    tbilisi: { lat: 41.7151, lon: 44.8271 },
    melbourne: { lat: -37.8136, lon: 144.9631 },
    sydney: { lat: -33.8688, lon: 151.2093 },
};

function parseBestMonths(str: string): Set<number> {
    const result = new Set<number>();
    if (!str) return result;

    const ranges = str.toLowerCase().replace(/[()]/g, '').split(',');
    for (const range of ranges) {
        const parts = range.trim().split(/[–—-]/);
        const startAbbr = parts[0]?.trim().slice(0, 3) || '';
        const endAbbr = parts.length > 1 ? parts[parts.length - 1]?.trim().slice(0, 3) : startAbbr;
        const s = MONTH_ABBR_MAP[startAbbr];
        const e = MONTH_ABBR_MAP[endAbbr];
        if (s == null || e == null) continue;

        let i = s;
        while (true) {
            result.add(i);
            if (i === e) break;
            i = (i + 1) % 12;
        }
    }

    return result;
}

function getMonthlyWeather(region: string, bestMonthsSet: Set<number>): MonthlyWeather[] {
    const base = REGION_TEMPS[region] || REGION_TEMPS.Europe;
    return MONTHS.map((name, i) => {
        const temp = base[i];
        let icon: WeatherIcon = 'sun';
        if (temp < 8) icon = 'snow';
        else if (temp < 15) icon = 'cloud';
        else if (!bestMonthsSet.has(i) && temp > 20) icon = 'rain';
        return { name, temp, icon, best: bestMonthsSet.has(i) };
    });
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const r = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    return r * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function estimateFlightHours(originCode: string, destName: string): number | null {
    const ORIGIN_COORDS: Record<string, { lat: number; lon: number }> = {
        JFK: { lat: 40.6413, lon: -73.7781 },
        LAX: { lat: 33.9425, lon: -118.4081 },
        ORD: { lat: 41.9742, lon: -87.9073 },
        MIA: { lat: 25.7959, lon: -80.287 },
        SFO: { lat: 37.6213, lon: -122.379 },
        SEA: { lat: 47.4502, lon: -122.3088 },
        BOS: { lat: 42.3656, lon: -71.0096 },
        DFW: { lat: 32.8998, lon: -97.0403 },
        ATL: { lat: 33.6407, lon: -84.4277 },
        DEN: { lat: 39.8561, lon: -104.6737 },
        LHR: { lat: 51.47, lon: -0.4543 },
        CDG: { lat: 49.0097, lon: 2.5479 },
        NRT: { lat: 35.772, lon: 140.3929 },
        DXB: { lat: 25.2532, lon: 55.3657 },
        SIN: { lat: 1.3644, lon: 103.9915 },
        BCN: { lat: 41.2974, lon: 2.0833 },
        FCO: { lat: 41.8003, lon: 12.2389 },
        AMS: { lat: 52.3105, lon: 4.7683 },
        IST: { lat: 41.2753, lon: 28.7519 },
        BKK: { lat: 13.69, lon: 100.7501 },
        SYD: { lat: -33.9461, lon: 151.1772 },
        YYZ: { lat: 43.6777, lon: -79.6248 },
        ICN: { lat: 37.4602, lon: 126.4407 },
        HKG: { lat: 22.308, lon: 113.9185 },
        TBS: { lat: 41.6692, lon: 44.9547 },
        OPO: { lat: 41.2481, lon: -8.6814 },
        LIS: { lat: 38.7756, lon: -9.1354 },
        FRA: { lat: 50.0379, lon: 8.5622 },
    };

    const orig = ORIGIN_COORDS[originCode];
    const nameLower = destName.toLowerCase();
    const dest = Object.entries(DEST_COORDS).find(([key]) => nameLower.includes(key))?.[1];
    if (!orig || !dest) return null;

    const km = haversineKm(orig.lat, orig.lon, dest.lat, dest.lon);
    const hours = km / 850;
    return Math.round(hours * 2) / 2;
}

function buildDest(slug?: string): DestinationView | null {
    const dest = destinations.find((d) => d.slug === slug);
    const feat = featuredTrips.find((t) => t.slug === slug);
    if (!dest && !feat) return null;

    const name = dest?.name || feat?.destination || 'Unknown';
    const country = dest?.country || '';
    const region = dest?.region || '';
    const tagline = dest?.tagline || feat?.tagline || '';
    const image = dest?.image || feat?.image || '';
    const price = dest?.price || feat?.startingPrice || 800;
    const match = dest?.match || feat?.match || 90;
    const bestMonths = dest?.bestMonths || feat?.quickInfo?.season || 'Year-round';
    const flightTime = dest?.flightTime || '~10h';
    const knownFor = dest?.knownFor || [];
    const highlights = dest?.highlights || [];
    const travelTips = dest?.travelTips || [];
    const categories = dest?.categories || feat?.categories || [];
    const gallery = feat?.gallery || [];

    const curatedGallery = destinationGalleries[slug || ''];
    const photos = curatedGallery
        ? curatedGallery
        : gallery.length >= 6
            ? gallery.slice(0, 6)
            : gallery.length >= 4
                ? [
                      ...gallery.slice(0, 4),
                      image,
                      'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200&h=480&fit=crop&q=80',
                  ]
                : [
                      image,
                      'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=500&h=520&fit=crop&q=80',
                      'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=500&h=400&fit=crop&q=80',
                      'https://images.unsplash.com/photo-1503220317375-aaad61436b1b?w=500&h=400&fit=crop&q=80',
                      'https://images.unsplash.com/photo-1548013146-72479768bada?w=500&h=400&fit=crop&q=80',
                      'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200&h=480&fit=crop&q=80',
                  ];

    const descParts: string[] = [];
    if (highlights.length > 0) {
        descParts.push(highlights.map((h) => h.description).join(' '));
    }
    if (knownFor.length > 0) {
        descParts.push(
            `Known for ${knownFor.join(', ')}, ${name} offers unforgettable experiences for every kind of traveller - from leisurely exploration to thrilling adventure.`,
        );
    }
    if (travelTips.length > 0) {
        descParts.push(`Travel tip: ${travelTips[0]}${travelTips[1] ? ` ${travelTips[1]}` : ''}`);
    }
    if (descParts.length === 0) {
        descParts.push(
            `${name} is a captivating destination that blends natural beauty with cultural richness. Whether you're seeking relaxation or adventure, this destination has something truly special waiting for you.`,
        );
    }

    const description = descParts.join('\n\n');
    const temp = `${18 + Math.round(Math.random() * 10)}°C`;
    const condition: 'Sunny' | 'Partly Cloudy' = Math.random() > 0.5 ? 'Partly Cloudy' : 'Sunny';

    return {
        slug: slug || '',
        name,
        country,
        region,
        tagline,
        image,
        price,
        match,
        bestMonths,
        flightTime,
        knownFor,
        categories,
        description,
        temp,
        condition,
        photos,
        monthlyWeather: getMonthlyWeather(region, parseBestMonths(bestMonths)),
    };
}

function getSimilar(dest: DestinationView): SimilarDestination[] {
    return destinations
        .filter((d) => d.slug !== dest.slug && d.categories.some((c) => dest.categories.includes(c)))
        .sort(() => Math.random() - 0.5)
        .slice(0, 4)
        .map((d) => ({
            slug: d.slug,
            name: d.name,
            country: d.country,
            price: d.price,
            image: d.image,
            tagline: d.tagline,
        }));
}

type DestinationPageProps = {
    slug?: string;
    inModal?: boolean;
};

export default function DestinationPage({ slug: slugProp, inModal = false }: DestinationPageProps) {
    const { slug: routeSlug } = useParams<{ slug: string }>();
    const initialSlug = slugProp ?? routeSlug;
    const navigate = useNavigate();
    const rootRef = useRef<HTMLDivElement | null>(null);
    const heroImgRef = useRef<HTMLDivElement | null>(null);
    const [activeSlug, setActiveSlug] = useState<string | undefined>(initialSlug);
    const [similarModalSlug, setSimilarModalSlug] = useState<string | null>(null);
    const { origin } = useOrigin() as OriginContextValue;
    const { saveTrip, removeSavedTrip, isTripSaved, profile } = useProfile();

    const dest = buildDest(activeSlug);

    useEffect(() => {
        setActiveSlug(initialSlug);
    }, [initialSlug]);

    useEffect(() => {
        if (inModal) {
            const modalScroller = rootRef.current?.closest('[data-modal-scroll-container="true"]') as HTMLElement | null;
            if (modalScroller) {
                modalScroller.scrollTop = 0;
                window.requestAnimationFrame(() => {
                    modalScroller.scrollTop = 0;
                });
                return;
            }
        }
        window.scrollTo(0, 0);
    }, [activeSlug, inModal]);

    const openSimilar = (nextSlug: string) => {
        if (inModal) {
            // Reuse the current modal content instead of stacking another modal.
            setActiveSlug(nextSlug);
            setSimilarModalSlug(null);
            return;
        }
        setSimilarModalSlug(nextSlug);
    };

    if (!dest) {
        return (
            <div className={styles.dp} style={{ padding: '160px 40px', textAlign: 'center' }}>
                <h2>Destination not found</h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>The trip you're looking for doesn't exist.</p>
                <Link to="/" style={{ color: 'var(--brand)', fontWeight: 600 }}>
                    Back to home
                </Link>
            </div>
        );
    }

    const similar = getSimilar(dest);
    const originCode = origin?.airport || origin?.code || 'JFK';

    const flightHours = useMemo(() => estimateFlightHours(originCode, dest.name), [originCode, dest.name]);

    const goChat = (msg: string) => navigate('/chat', { state: { message: msg } });

    // Both "Plan the trip" buttons open the same conversation. Not every
    // destination has a country, so fall back to the region the way the hero
    // heading above does rather than sending "Plan a trip to Bali, undefined".
    const planTripMessage = `Plan a trip to ${dest.name}, ${dest.country || dest.region}`;
    const saved = isTripSaved(dest.name);
    const toggleSave = () => {
        const existing = profile.savedTrips.find(
            trip => trip.destination.toLowerCase() === dest.name.toLowerCase(),
        );
        if (existing) {
            removeSavedTrip(existing.id);
        } else {
            saveTrip({
                title: dest.name,
                destination: dest.name,
                slug: activeSlug,
                note: dest.tagline,
            });
        }
    };

    return (
        <div ref={rootRef} className={`${styles.dp}${inModal ? ` ${styles.inModal}` : ''}`}>
            <section className={styles.dpHero}>
                <div className={styles.dpHeroImg} ref={heroImgRef} style={{ backgroundImage: `url(${dest.image})` }} />
                <div className={styles.dpHeroScrim} />

                <button
                    className={`${styles.dpHeroHeart}${saved ? ` ${styles.dpHeroHeartSaved}` : ''}`}
                    onClick={toggleSave}
                    type="button"
                    aria-label={saved ? `Remove ${dest.name} from saved trips` : `Save ${dest.name}`}
                >
                    <SVGHeart width="22" height="22" fill={saved ? 'currentColor' : 'none'} />
                </button>

                <div className={styles.dpHeroCenter}>
                    <h1 className={styles.dpHeroName}>
                        {dest.name}, {dest.country || dest.region}
                    </h1>
                    <p className={styles.dpHeroTagline}>{dest.tagline}</p>
                </div>

                <button
                    className={styles.dpHeroCta}
                    onClick={() => goChat(planTripMessage)}
                >
                    Plan the trip <SVGArrowRight width="16" height="16" />
                </button>
            </section>

            <section className={styles.dpWidgets}>
                <div className={styles.dpWidget}>
                    <div className={styles.dpWidgetIcon}>{dest.condition === 'Sunny' ? <SVGSun width="26" height="26" /> : <SVGCloud width="26" height="26" />}</div>
                    <div className={styles.dpWidgetText}>
                        <span className={styles.dpWidgetValue}>{dest.temp}</span>
                        <span className={styles.dpWidgetLabel}>{dest.condition}</span>
                    </div>
                </div>
                <div className={styles.dpWidget}>
                    <div className={styles.dpWidgetIcon}>
                        <SVGCalendar width="26" height="26" />
                    </div>
                    <div className={styles.dpWidgetText}>
                        <span className={styles.dpWidgetValue}>{dest.bestMonths}</span>
                        <span className={styles.dpWidgetLabel}>Best months</span>
                    </div>
                </div>
                <div className={styles.dpWidget}>
                    <div className={styles.dpWidgetIcon}>
                        <SVGPlane width="26" height="26" />
                    </div>
                    <div className={styles.dpWidgetText}>
                        <span className={styles.dpWidgetValue}>{dest.flightTime}</span>
                        <span className={styles.dpWidgetLabel}>Flight time</span>
                    </div>
                </div>
                {flightHours && (
                    <div className={`${styles.dpWidget} ${styles.dpWidgetAccent}`}>
                        <div className={styles.dpWidgetIcon}>
                            <SVGClock width="26" height="26" />
                        </div>
                        <div className={styles.dpWidgetText}>
                            <span className={styles.dpWidgetValue}>{flightHours}h</span>
                            <span className={styles.dpWidgetLabel}>from {origin?.city || originCode}</span>
                        </div>
                    </div>
                )}
                <div className={styles.dpWidget}>
                    <div className={styles.dpWidgetIcon}>
                        <SVGDollarSign width="26" height="26" />
                    </div>
                    <div className={styles.dpWidgetText}>
                        <span className={styles.dpWidgetValue}>From ${dest.price}</span>
                        <span className={styles.dpWidgetLabel}>Per person</span>
                    </div>
                </div>
            </section>

            <section className={styles.dpDescription}>
                <h2 className={styles.dpDescTitle}>Discover the Spirit of {dest.name}</h2>
                {dest.description.split('\n\n').map((para, i) => (
                    <p key={i} className={styles.dpDescText}>
                        {para}
                    </p>
                ))}
                {dest.knownFor.length > 0 && (
                    <div className={styles.dpKnownFor}>
                        {dest.knownFor.slice(0, 6).map((k, i) => (
                            <span key={i} className={styles.dpKnownTag}>
                                {k}
                            </span>
                        ))}
                    </div>
                )}
            </section>

            <section className={styles.dpWeather}>
                <h2 className={styles.dpSectionTitle}>Weather by month</h2>
                <div className={styles.dpWeatherGrid}>
                    {dest.monthlyWeather.map((m, i) => {
                        const isCurrent = i === new Date().getMonth();
                        return (
                            <div
                                key={i}
                                className={`${styles.dpWeatherCol}${isCurrent ? ` ${styles.dpWeatherColActive}` : ''}${m.best ? ` ${styles.dpWeatherColBest}` : ''}`}
                            >
                                <span className={styles.dpWeatherMonth}>{m.name}</span>
                                <span className={styles.dpWeatherIcon}>
                                    {m.icon === 'sun' && <SVGSun width="18" height="18" />}
                                    {m.icon === 'cloud' && <SVGCloud width="18" height="18" />}
                                    {m.icon === 'rain' && <SVGCloudRain width="18" height="18" />}
                                    {m.icon === 'snow' && <SVGCloudSnow width="18" height="18" />}
                                </span>
                                <span className={styles.dpWeatherTemp}>{m.temp}°C</span>
                            </div>
                        );
                    })}
                </div>
                <p className={styles.dpWeatherBest}>Best time to visit: {dest.bestMonths}</p>
            </section>

            <section className={styles.dpPhotos}>
                <div className={styles.dpPhotosWrap}>
                    <div className={styles.dpPhotosGrid}>
                        {dest.photos.map((src, i) => (
                            <div key={i} className={`${styles.dpPhotoCell} ${styles[`dpPhotoCell${i}` as keyof typeof styles] || ''}`}>
                                <img src={src} alt={`${dest.name} - ${i + 1}`} loading="lazy" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className={styles.dpBottomCta}>
                <h2>Ready to explore {dest.name}?</h2>
                <button
                    className={styles.dpBottomCtaBtn}
                    onClick={() => goChat(planTripMessage)}
                >
                    Plan the trip <SVGArrowRight width="18" height="18" />
                </button>
            </section>

            {similar.length > 0 && (
                <section className={styles.dpSimilar}>
                    <h2 className={styles.dpSectionTitle}>You might also like</h2>
                    <div className={styles.dpSimilarRow}>
                        {similar.map((s) => (
                            <div
                                key={s.slug}
                                className={styles.dpSimilarCard}
                                role="button"
                                tabIndex={0}
                                onClick={() => openSimilar(s.slug)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openSimilar(s.slug);
                                    }
                                }}
                            >
                                <div className={styles.dpSimilarImg}>
                                    <img src={s.image} alt={s.name} loading="lazy" />
                                </div>
                                <div className={styles.dpSimilarBody}>
                                    <div className={styles.dpSimilarName}>{s.name}</div>
                                    <div className={styles.dpSimilarCountry}>{s.country}</div>
                                    <div className={styles.dpSimilarPrice}>From ${s.price}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {!inModal && (
                <Modal isOpen={Boolean(similarModalSlug)} onClose={() => setSimilarModalSlug(null)} size="page">
                    {similarModalSlug && <DestinationPage slug={similarModalSlug} inModal />}
                </Modal>
            )}
        </div>
    );
}
